import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, X, Upload, FolderOpen } from 'lucide-react';
import { imageFallback, resolveImageUrl } from '../../lib/imageUtils';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const AdminCategories = () => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [subForm, setSubForm] = useState({ name: '', description: '', category_id: '' });
  const [editingSub, setEditingSub] = useState(null);
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    fetchCategories(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchCategories = async () => {
    try {
      const [catRes, subRes] = await Promise.all([
        axios.get(`${BACKEND}/api/categories`),
        axios.get(`${BACKEND}/api/subcategories`)
      ]);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setForm({ name: '', description: '' });
    setImageFile(null);
    setEditing(null);
    setShowForm(false);
  };

  const resetSubForm = () => {
    setSubForm({ name: '', description: '', category_id: '' });
    setEditingSub(null);
  };

  const openEdit = (c) => {
    setForm({ name: c.name, description: c.description || '' });
    setEditing(c.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('description', form.description);
    if (imageFile) fd.append('image', imageFile);

    try {
      if (editing) {
        await axios.put(`${BACKEND}/api/categories/${editing}`, fd);
      } else {
        await axios.post(`${BACKEND}/api/categories`, fd);
      }
      resetForm();
      fetchCategories();
    } catch (err) { alert('Error saving category'); console.error(err); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    await axios.delete(`${BACKEND}/api/categories/${id}`);
    fetchCategories();
  };

  const openSubEdit = (s) => {
    setSubForm({ name: s.name, description: s.description || '', category_id: s.category_id });
    setEditingSub(s.id);
  };

  const handleSubSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingSub) {
        await axios.put(`${BACKEND}/api/subcategories/${editingSub}`, subForm);
      } else {
        await axios.post(`${BACKEND}/api/subcategories`, subForm);
      }
      resetSubForm();
      fetchCategories();
    } catch (err) { alert('Error saving subcategory'); console.error(err); }
  };

  const handleSubDelete = async (id) => {
    if (!window.confirm('Delete this subcategory?')) return;
    await axios.delete(`${BACKEND}/api/subcategories/${id}`);
    fetchCategories();
  };

  return (
    <div className="space-y-6" data-testid="admin-categories-page">
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-light tracking-tighter text-white">Categories</h1>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="px-6 py-3 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-colors flex items-center space-x-2"
          data-testid="add-category-button">
          <Plus size={18} /><span>Add Category</span>
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" data-testid="category-form-modal">
          <div className="bg-[#141414] border border-white/10 p-8 w-full max-w-lg">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-light text-white">{editing ? 'Edit Category' : 'Add Category'}</h2>
              <button onClick={resetForm} className="text-[#A3A3A3] hover:text-white" data-testid="close-category-form"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Name</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required
                  className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none" data-testid="category-name-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3}
                  className="w-full bg-transparent border border-white/20 focus:border-[#D4AF37] text-white p-3 outline-none resize-none" data-testid="category-description-input" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Image</label>
                <label className="flex items-center justify-center border-2 border-dashed border-white/20 hover:border-[#D4AF37] p-6 cursor-pointer transition-colors">
                  <div className="text-center space-y-2">
                    <Upload size={24} className="mx-auto text-[#A3A3A3]" />
                    <p className="text-sm text-[#A3A3A3]">{imageFile ? imageFile.name : 'Click to upload'}</p>
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={e => setImageFile(e.target.files[0])} data-testid="category-image-upload" />
                </label>
              </div>
              <button type="submit" className="w-full py-4 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-colors" data-testid="save-category-button">
                {editing ? 'Update Category' : 'Create Category'}
              </button>
            </form>
          </div>
        </div>
      )}

      {loading ? <p className="text-[#A3A3A3]">Loading...</p> : (
        <div className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map(c => (
              <div key={c.id} className="bg-[#141414] border border-white/10 p-6 space-y-4" data-testid={`category-card-admin-${c.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-[#0A0A0A] flex items-center justify-center">
                      {c.image ? <img src={resolveImageUrl(c.image, BACKEND)} alt="" onError={imageFallback} className="w-full h-full object-cover" /> : <FolderOpen size={20} className="text-[#D4AF37]" />}
                    </div>
                    <div>
                      <h3 className="text-xl font-light text-white">{c.name}</h3>
                      <p className="text-sm text-[#A3A3A3]">{c.description || 'No description'}</p>
                      <p className="text-xs text-[#D4AF37] mt-1">{subcategories.filter(s => s.category_id === c.id).length} subcategories</p>
                    </div>
                  </div>
                  <div className="flex space-x-2">
                    <button onClick={() => openEdit(c)} className="text-[#A3A3A3] hover:text-white" data-testid={`edit-category-${c.id}`}><Pencil size={16} /></button>
                    <button onClick={() => handleDelete(c.id)} className="text-[#A3A3A3] hover:text-[#FF3B30]" data-testid={`delete-category-${c.id}`}><Trash2 size={16} /></button>
                  </div>
                </div>
              </div>
            ))}
            {categories.length === 0 && <p className="text-[#A3A3A3] col-span-full text-center py-8">No categories yet</p>}
          </div>

          <div className="space-y-6" data-testid="admin-subcategories-section">
            <div className="bg-[#141414] border border-white/10 p-6 space-y-6">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                <div>
                  <h2 className="text-2xl font-light text-white">{editingSub ? 'Edit Subcategory' : 'Create Subcategory'}</h2>
                  <p className="text-sm text-[#A3A3A3]">Choose a parent category, then add the subcategory details.</p>
                </div>
                {editingSub && (
                  <button type="button" onClick={resetSubForm}
                    className="self-start px-4 py-2 border border-white/20 text-[#A3A3A3] hover:text-white hover:border-white transition-colors text-xs uppercase tracking-wider"
                    data-testid="cancel-subcategory-edit">
                    Cancel Edit
                  </button>
                )}
              </div>

              <form onSubmit={handleSubSubmit} className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Parent Category</label>
                    <select value={subForm.category_id} onChange={e => setSubForm({ ...subForm, category_id: e.target.value })} required
                      className="w-full bg-[#0A0A0A] border border-white/20 text-white px-3 py-3 outline-none focus:border-[#D4AF37]" data-testid="subcategory-category-select">
                      <option value="">Select Category</option>
                      {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Subcategory Name</label>
                    <input type="text" value={subForm.name} onChange={e => setSubForm({ ...subForm, name: e.target.value })} required placeholder="Gold Rings"
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none" data-testid="subcategory-name-input" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-2">Description</label>
                    <input type="text" value={subForm.description} onChange={e => setSubForm({ ...subForm, description: e.target.value })} placeholder="Optional"
                      className="w-full bg-transparent border-b border-white/20 focus:border-[#D4AF37] text-white py-3 outline-none" data-testid="subcategory-description-input" />
                  </div>
                </div>

                <div className="flex justify-end">
                  <button type="submit" className="w-full md:w-auto min-w-48 py-3 px-8 bg-[#D4AF37] text-black font-medium tracking-wide uppercase text-sm hover:bg-[#F1E5AC] transition-colors" data-testid="save-subcategory-button">
                    {editingSub ? 'Update Subcategory' : 'Create Subcategory'}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-[#141414] border border-white/10 p-6 space-y-4">
              <div>
                <h2 className="text-2xl font-light text-white">Subcategory List</h2>
                <p className="text-sm text-[#A3A3A3]">Existing subcategories grouped by their parent category.</p>
              </div>

              <div className="overflow-x-auto">
              <table className="w-full" data-testid="subcategories-table">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Name</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Category</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Description</th>
                    <th className="text-left py-3 px-4 text-xs uppercase tracking-wider text-[#A3A3A3]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {subcategories.map(s => (
                    <tr key={s.id} className="border-b border-white/5">
                      <td className="py-4 px-4 text-white">{s.name}</td>
                      <td className="py-4 px-4 text-[#A3A3A3]">{s.category_name}</td>
                      <td className="py-4 px-4 text-[#A3A3A3]">{s.description || '-'}</td>
                      <td className="py-4 px-4">
                        <div className="flex gap-3">
                          <button onClick={() => openSubEdit(s)} className="text-[#A3A3A3] hover:text-white" data-testid={`edit-subcategory-${s.id}`}><Pencil size={16} /></button>
                          <button onClick={() => handleSubDelete(s.id)} className="text-[#A3A3A3] hover:text-[#FF3B30]" data-testid={`delete-subcategory-${s.id}`}><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {subcategories.length === 0 && <p className="text-center py-8 text-[#A3A3A3]">No subcategories yet</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCategories;
